import { apiService } from './api';

// ============================================================================
// TYPES
// ============================================================================

export type ReconciliationStatus = 'match' | 'minor_variance' | 'major_variance' | 'critical' | 'never_run';
export type AnomalySeverity = 'critical' | 'warning' | 'info';
export type AnomalyType = 'usage_spike' | 'unusual_pattern' | 'after_hours' | 'single_user_dominance' | 'rapid_growth';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired';
export type PricingNotificationStatus = 'pending' | 'acknowledged' | 'applied' | 'cancelled';

export interface ComplianceDashboard {
  timestamp: string;
  reconciliation: {
    lastRun: string | null;
    status: ReconciliationStatus;
    tokenVariancePercent: number;
    minuteVariancePercent: number;
    isRunning: boolean;
  };
  pendingApprovals: {
    usageResets: number;
    bulkAssigns: number;
    pricingChanges: number;
    total: number;
  };
  customPlans: {
    totalActive: number;
    needingReview: number;
    expired: number;
  };
  quotaHealth: {
    partnersNearLimit: number;
  };
  alerts: {
    criticalCount: number;
    warningCount: number;
  };
}

export interface UsageResetRequest {
  id: string;
  organizationId: {
    id: string;
    name: string;
  };
  periodKey: string;
  reason: string;
  snapshotData?: {
    encountersUsed: number;
    transcriptionMinutesUsed: number;
    totalTokens: number;
  };
  requestedBy: {
    id: string;
    name: string;
    email: string;
  };
  requestedAt: string;
  status: ApprovalStatus;
  approvedBy?: {
    id: string;
    name: string;
    email: string;
  };
  approvedAt?: string;
  rejectionReason?: string;
}

export interface BulkAssignRequest {
  id: string;
  organizations: Array<{
    organizationId: string;
    organizationName?: string;
  }>;
  planId: string;
  planName?: string;
  effectiveDate?: string;
  justification: string;
  requestedBy: {
    id: string;
    name: string;
    email: string;
  };
  requestedAt: string;
  status: ApprovalStatus;
  approvedBy?: {
    id: string;
    name: string;
    email: string;
  };
  approvedAt?: string;
  rejectionReason?: string;
}

export interface PricingChangeNotification {
  id: string;
  organizationId: {
    id: string;
    name: string;
  };
  organizationName?: string;
  currentPricing: {
    planId?: string;
    planName?: string;
    monthlyPerClinicalUser: number;
    monthlyPerStaffUser: number;
    encountersPerClinicalUser: number;
    minutesPerClinicalUser: number;
    perEncounterOveragePrice: number;
    perMinuteOveragePrice: number;
    currency: string;
  };
  newPricing: {
    planId?: string;
    planName?: string;
    monthlyPerClinicalUser: number;
    monthlyPerStaffUser: number;
    encountersPerClinicalUser: number;
    minutesPerClinicalUser: number;
    perEncounterOveragePrice: number;
    perMinuteOveragePrice: number;
    currency: string;
  };
  changeImpact: {
    percentageChange: number;
    estimatedMonthlyCostChange: number;
    requiresAcknowledgment: boolean;
    summary: string;
  };
  notifiedAt: string;
  effectiveDate: string;
  gracePeriodEnd?: string;
  notifiedVia: 'email' | 'dashboard' | 'both';
  status: PricingNotificationStatus;
  acknowledgedAt?: string;
  acknowledgedBy?: {
    id: string;
    name: string;
    email: string;
  };
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
}

export interface AnomalyAlert {
  type: AnomalyType;
  severity: AnomalySeverity;
  organizationId: string;
  organizationName?: string;
  userId?: string;
  userName?: string;
  details: {
    baseline: number;
    current: number;
    multiplier: number;
    timeWindow: string;
    metric?: string;
  };
  message: string;
  detectedAt: string;
  recommendedAction?: string;
}

export interface CustomPlan {
  id: string;
  organizationId: {
    id: string;
    name: string;
  };
  planName: string;
  status: string;
  isCustomPlan: boolean;
  customPlanDetails?: {
    justification: string;
    approvedBy?: {
      id: string;
      name: string;
      email: string;
    };
    approvedAt?: string;
    requestedBy?: {
      id: string;
      name: string;
      email: string;
    };
    expiresAt?: string;
    nextReviewAt?: string;
    maxLimits?: {
      encounters: number;
      minutes: number;
      users: number;
    };
  };
  limits: {
    encountersPerPeriod: number;
    transcriptionMinutesPerPeriod: number;
    maxBillableUsers: number;
    maxNonBillableUsers: number;
  };
  createdAt: string;
}

export interface PartnerQuota {
  id: string;
  name: string;
  slug: string;
  quotas?: {
    maxOrganizations: number;
    maxUsersPerOrg: number;
    currentActiveOrgs: number;
    maxStorageGB: number;
  };
  quotaTracking?: {
    totalOrgsCreatedAllTime: number;
    quotaAlertLevel: 'none' | 'warning' | 'critical';
    lastQuotaAlertAt?: string;
  };
  quotaStatus: {
    percentUsed: number;
    isNearLimit: boolean;
    isAtLimit: boolean;
    alertLevel: string;
  };
}

export interface ReconciliationResult {
  period: string;
  periodStart: string;
  periodEnd: string;
  reconciliationDate: string;
  mongoDbTokens: number;
  azureTokens: number;
  mongoDbMinutes: number;
  azureMinutes: number;
  tokenVariance: number;
  tokenVariancePercent: number;
  minuteVariance: number;
  minuteVariancePercent: number;
  status: ReconciliationStatus;
  statusMessage: string;
  details: {
    byService: Record<string, { mongodb: number; azure: number; variance: number; variancePercent: number }>;
    byOrganization: Array<{
      organizationId: string;
      organizationName?: string;
      mongodbTokens: number;
      mongodbMinutes: number;
      variance?: number;
    }>;
    topVarianceOrgs: Array<{
      organizationId: string;
      organizationName?: string;
      mongodbTokens: number;
      mongodbMinutes: number;
      variance?: number;
    }>;
  };
  alerts: {
    sent: boolean;
    recipients?: string[];
    alertLevel?: 'info' | 'warning' | 'critical';
  };
  executedBy?: string;
  executionDurationMs: number;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

// ============================================================================
// SERVICE
// ============================================================================

export const complianceService = {
  // Dashboard
  async getDashboard(): Promise<ComplianceDashboard> {
    const response = await apiService.get<ApiResponse<ComplianceDashboard>>('/v1/compliance/dashboard');
    return response.data;
  },

  // Pending Approvals
  async getPendingApprovals(): Promise<{
    usageResets: UsageResetRequest[];
    bulkAssigns: BulkAssignRequest[];
    pricingChanges: PricingChangeNotification[];
  }> {
    const response = await apiService.get<ApiResponse<{
      usageResets: UsageResetRequest[];
      bulkAssigns: BulkAssignRequest[];
      pricingChanges: PricingChangeNotification[];
    }>>('/v1/compliance/pending-approvals');
    return response.data;
  },

  // Usage Reset Requests
  async approveUsageReset(requestId: string): Promise<UsageResetRequest> {
    const response = await apiService.post<ApiResponse<UsageResetRequest>>(
      `/v1/compliance/usage-reset-requests/${requestId}/approve`
    );
    return response.data;
  },

  async rejectUsageReset(requestId: string, reason: string): Promise<UsageResetRequest> {
    const response = await apiService.post<ApiResponse<UsageResetRequest>>(
      `/v1/compliance/usage-reset-requests/${requestId}/reject`,
      { reason }
    );
    return response.data;
  },

  // Bulk Assign Requests
  async approveBulkAssign(requestId: string): Promise<BulkAssignRequest> {
    const response = await apiService.post<ApiResponse<BulkAssignRequest>>(
      `/v1/compliance/bulk-assign-requests/${requestId}/approve`
    );
    return response.data;
  },

  async rejectBulkAssign(requestId: string, reason: string): Promise<BulkAssignRequest> {
    const response = await apiService.post<ApiResponse<BulkAssignRequest>>(
      `/v1/compliance/bulk-assign-requests/${requestId}/reject`,
      { reason }
    );
    return response.data;
  },

  // Anomalies
  async getAnomalies(params?: {
    organizationId?: string;
    severity?: AnomalySeverity;
  }): Promise<{
    total: number;
    bySeverity: {
      critical: number;
      warning: number;
      info: number;
    };
    alerts: AnomalyAlert[];
  }> {
    const response = await apiService.get<ApiResponse<{
      total: number;
      bySeverity: {
        critical: number;
        warning: number;
        info: number;
      };
      alerts: AnomalyAlert[];
    }>>('/v1/compliance/anomalies', params);
    return response.data;
  },

  // Custom Plans
  async getCustomPlans(params?: {
    status?: string;
    needsReview?: boolean;
  }): Promise<{
    total: number;
    categorized: {
      expired: CustomPlan[];
      needsReview: CustomPlan[];
      active: CustomPlan[];
    };
    plans: CustomPlan[];
  }> {
    const response = await apiService.get<ApiResponse<{
      total: number;
      categorized: {
        expired: CustomPlan[];
        needsReview: CustomPlan[];
        active: CustomPlan[];
      };
      plans: CustomPlan[];
    }>>('/v1/compliance/custom-plans', {
      ...params,
      needsReview: params?.needsReview ? 'true' : undefined
    });
    return response.data;
  },

  // Partner Quotas
  async getPartnerQuotas(): Promise<{
    total: number;
    summary: {
      atLimit: number;
      nearLimit: number;
      healthy: number;
    };
    categorized: {
      atLimit: PartnerQuota[];
      nearLimit: PartnerQuota[];
      healthy: PartnerQuota[];
    };
    partners: PartnerQuota[];
  }> {
    const response = await apiService.get<ApiResponse<{
      total: number;
      summary: {
        atLimit: number;
        nearLimit: number;
        healthy: number;
      };
      categorized: {
        atLimit: PartnerQuota[];
        nearLimit: PartnerQuota[];
        healthy: PartnerQuota[];
      };
      partners: PartnerQuota[];
    }>>('/v1/compliance/partner-quotas');
    return response.data;
  },

  // Reconciliation
  async getReconciliationStatus(): Promise<{
    isRunning: boolean;
    lastResult: ReconciliationResult | null;
    nextScheduledRun: string;
  }> {
    const response = await apiService.get<ApiResponse<{
      isRunning: boolean;
      lastResult: ReconciliationResult | null;
      nextScheduledRun: string;
    }>>('/v1/compliance/reconciliation/status');
    return response.data;
  },

  async triggerReconciliation(periodKey?: string): Promise<{
    periodKey: string;
    status: string;
  }> {
    const response = await apiService.post<ApiResponse<{
      periodKey: string;
      status: string;
    }>>('/v1/compliance/reconciliation/trigger', { periodKey });
    return response.data;
  },

  // Pricing Changes
  async getPricingChanges(status?: PricingNotificationStatus): Promise<{
    total: number;
    summary: {
      pending: number;
      acknowledged: number;
      applied: number;
      cancelled: number;
      overdue: number;
    };
    overdue: PricingChangeNotification[];
    notifications: PricingChangeNotification[];
  }> {
    const response = await apiService.get<ApiResponse<{
      total: number;
      summary: {
        pending: number;
        acknowledged: number;
        applied: number;
        cancelled: number;
        overdue: number;
      };
      overdue: PricingChangeNotification[];
      notifications: PricingChangeNotification[];
    }>>('/v1/compliance/pricing-changes', { status });
    return response.data;
  },
};
